import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Statik import - JSON dosyalarını doğrudan yükle
import trTranslations from '../locales/tr.json';
import enTranslations from '../locales/en.json';

const LanguageContext = createContext();

// JSON import'ları default export olarak gelir, direkt kullan
const translations = {
    tr: trTranslations.default || trTranslations,
    en: enTranslations.default || enTranslations
};

// Debug: Çevirilerin yüklendiğini kontrol et
if (process.env.NODE_ENV === 'development') {
    console.log('Translations loaded:', {
        tr: Object.keys(translations.tr || {}),
        en: Object.keys(translations.en || {})
    });
}

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        // localStorage'dan dil tercihini yükle
        const savedLanguage = localStorage.getItem('film_app_language');
        if (savedLanguage && (savedLanguage === 'tr' || savedLanguage === 'en')) {
            return savedLanguage;
        }
        // Tarayıcı dilini tespit et
        const browserLang = navigator.language.split('-')[0];
        return (browserLang === 'tr' || browserLang === 'en') ? browserLang : 'en';
    });

    // Desteklenen diller
    const supportedLanguages = {
        tr: { code: 'tr', name: 'Türkçe', locale: 'tr-TR' },
        en: { code: 'en', name: 'English', locale: 'en-US' }
    };

    useEffect(() => {
        // localStorage'a kaydet
        localStorage.setItem('film_app_language', language);
        // HTML lang attribute'unu güncelle
        document.documentElement.lang = language;
    }, [language]);

    const changeLanguage = (langCode) => {
        if (supportedLanguages[langCode]) {
            setLanguage(langCode);
        }
    };

    const t = useCallback((key, params = {}) => {
        if (!key) return '';
        
        const keys = key.split('.');
        let value = translations[language] || translations['en'];
        
        // Nested key'leri takip et
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Fallback to English
                let fallbackValue = translations['en'];
                for (const fk of keys) {
                    if (fallbackValue && typeof fallbackValue === 'object' && fk in fallbackValue) {
                        fallbackValue = fallbackValue[fk];
                    } else {
                        console.warn(`Translation key not found: ${key}`);
                        return key; // Key bulunamazsa key'i döndür
                    }
                }
                value = fallbackValue;
                break;
            }
        }

        // Parametreleri değiştir
        if (typeof value === 'string' && params && Object.keys(params).length > 0) {
            return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
                return params[paramKey] !== undefined ? params[paramKey] : match;
            });
        }

        return typeof value === 'string' ? value : key;
    }, [language]);

    const formatDate = (date, options = {}) => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);
        const locale = supportedLanguages[language].locale;
        
        const defaultOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            ...options
        };

        return dateObj.toLocaleDateString(locale, defaultOptions);
    };

    const formatDateTime = (date, options = {}) => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);
        const locale = supportedLanguages[language].locale;
        
        const defaultOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            ...options
        };

        return dateObj.toLocaleDateString(locale, defaultOptions);
    };

    const formatNumber = (number, options = {}) => {
        if (number === null || number === undefined) return '';
        const locale = supportedLanguages[language].locale;
        return new Intl.NumberFormat(locale, options).format(number);
    };

    const value = {
        language,
        locale: supportedLanguages[language].locale,
        changeLanguage,
        supportedLanguages,
        t,
        formatDate,
        formatDateTime,
        formatNumber
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

